import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Avatar,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Collapse,
  Badge,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  alpha,
  Link,
  LinearProgress,
  Snackbar,
  Skeleton,
  styled,
  Stack
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  EmojiEvents as RewardIcon,
  AddTask as TaskIcon,
  School as LearningIcon,
  People as CommunityIcon,
  Forum as ForumIcon,
  Lightbulb as ContentIcon,
  EventAvailable as EventIcon,
  CheckCircle as CompletedIcon,
  ArrowForward as ArrowIcon,
  Info as InfoIcon,
  HourglassFull as PendingIcon,
  Cancel as RejectedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  CloudUpload as UploadIcon,
  AccessTime as TimeIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Pending as InReviewIcon,
  Refresh as RefreshIcon,
  Description as FileIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as DocumentIcon,
  School as SchoolIcon,
  Replay as ReplayIcon
} from '@mui/icons-material';
import { tenantTokenService, tokenWalletService, tokenEarningService } from '../../../services/skillcoinService';
import { formatDistanceToNow } from 'date-fns';

// Define status constants for clarity
const SUBMISSION_STATUS = {
  SUBMITTED: 'submitted',
  IN_REVIEW: 'in_review',
  APPROVED: 'approved',
  REJECTED: 'rejected'
};

// Define getActivityColor before using it in styled components
const getActivityColor = (activityType, theme) => {
  switch (activityType) {
    case 'content_creation':
      return theme.palette.primary.main;
    case 'knowledge_sharing':
      return theme.palette.info.main;
    case 'forum_activity':
      return theme.palette.success.main;
    case 'event_participation':
      return theme.palette.warning.main;
    case 'learning':
      return theme.palette.secondary.main;
    case 'community_support':
      return theme.palette.error.main;
    case 'referral':
      return '#8e44ad';
    case 'social_media':
      return '#3498db';
    case 'contributions':
      return '#2ecc71';
    case 'feedback':
      return '#e74c3c';
    default:
      return theme.palette.grey[500];
  }
};

// Simplify the getStatusColor function to avoid undefined color issues
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'approved':
    case 'completed':
      return { color: 'success', variant: 'filled' };
    case 'in_review':
    case 'in progress':
      return { color: 'warning', variant: 'outlined' };
    case 'submitted':
    case 'active':
      return { color: 'primary', variant: 'filled' };
    case 'rejected':
    case 'expired':
      return { color: 'error', variant: 'outlined' };
    case 'available':
      return { color: 'info', variant: 'outlined' };
    default:
      return { color: 'default', variant: 'outlined' };
  }
};

// Custom styled components
const ActivityCard = styled(motion.div)(({ theme, type }) => {
  const baseColor = getActivityColor(type, theme) || theme.palette.primary.main;

  return {
    position: 'relative',
    borderRadius: theme.shape.borderRadius * 2,
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    height: '100%',
    background: `linear-gradient(145deg, ${alpha(baseColor, 0.05)} 0%, ${alpha(baseColor, 0.02)} 100%)`,
    boxShadow: `0 4px 20px ${alpha(baseColor, 0.15)}`,
    border: `1px solid ${alpha(baseColor, 0.2)}`,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: `0 8px 30px ${alpha(baseColor, 0.25)}`,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '4px',
      background: baseColor,
    }
  };
});

const ActivityHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`
}));

const CircleDecoration = styled('div')(({ theme, size = 100, top, right, left, bottom, opacity = 0.1 }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '50%',
  background: alpha(theme.palette.common.white, opacity),
  top: top || 'auto',
  right: right || 'auto',
  left: left || 'auto',
  bottom: bottom || 'auto',
  zIndex: 0,
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: theme.shape.borderRadius * 2,
  position: 'relative',
  overflow: 'hidden',
  marginBottom: theme.spacing(3),
}));

const DropZone = styled(Box)(({ theme, isDragActive }) => ({
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  textAlign: 'center',
  backgroundColor: isDragActive ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.background.default, 0.5),
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.main
  }
}));

const EarningOpportunities = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [earningActivities, setEarningActivities] = useState([]);
  const [error, setError] = useState(null);
  const [completedActivities, setCompletedActivities] = useState([]);
  const [submissionHistory, setSubmissionHistory] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [submissionForm, setSubmissionForm] = useState({ evidence: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [walletData, setWalletData] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchEarningActivities();
    fetchWalletData();
    fetchSubmissionHistory();
  }, []);

  useEffect(() => {
    if (tabValue === 0) {
      fetchEarningActivities();
    }
  }, [tabValue]);

  const fetchEarningActivities = async () => {
    setLoading(true);
    try {
      console.log('Fetching earning activities from API');

      const tenantId = localStorage.getItem('tenantId');
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');

      if (!tenantId || !authToken) {
        throw new Error('Missing tenant ID or authentication token');
      }

      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const url = `${apiBaseUrl}/v1/skillcoin/activities`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'x-tenant-id': tenantId
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch activities: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('Earning activities response:', responseData);
      const activities = responseData.data || [];
      setEarningActivities(activities);
      setError(null);
    } catch (err) {
      console.error('Error fetching earning activities:', err);
      setError('Failed to load earning opportunities. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchWalletData = async () => {
    try {
      console.log('Fetching wallet data...');
      setError(null);

      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      // Make a direct API call to ensure we get the latest data
      const tenantId = localStorage.getItem('tenantId');
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');

      if (!tenantId || !authToken) {
        throw new Error('Missing tenant ID or authentication token');
      }

      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      
      console.log('Making direct API call to fetch wallet data...');
      const response = await fetch(`${apiBaseUrl}/v1/skillcoin/wallets?_t=${timestamp}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'x-tenant-id': tenantId,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch wallet data: ${response.status}`);
      }

      const data = await response.json();
      console.log('Direct API wallet response:', data);

      let walletData = null;
      
      // Handle different response formats
      if (data.success && data.data && data.data.length > 0) {
        walletData = data.data[0];
      } else if (Array.isArray(data) && data.length > 0) {
        walletData = data[0];
      } else if (data.data && Array.isArray(data.data) && data.data.length > 0) {
        walletData = data.data[0];
      }

      // Ensure balance is parsed as a number
      if (walletData) {
        walletData.balance = parseFloat(walletData.balance || 0);
        console.log('Wallet balance parsed:', walletData.balance);
        setWalletData(walletData);
      } else {
        console.log('No wallet data found, setting default placeholder');
        setWalletData({
          balance: 0,
          token: { symbol: 'MEC' }
        });
      }
    } catch (err) {
      console.error('Error fetching wallet data:', err);
      setWalletData({
        balance: 0,
        token: { symbol: 'MEC' }
      });
    }
  };

  const fetchSubmissionHistory = async () => {
    try {
      console.log('Fetching submission history from API');
      setError(null);
      const response = await tokenEarningService.getSubmissionHistory();
      const data = response?.data || [];
      console.log('Submission history response:', data);
      setSubmissionHistory(data);
      const approvedSubmissions = data.filter(sub =>
        sub.status === SUBMISSION_STATUS.APPROVED
      );
      setCompletedActivities(approvedSubmissions.map(sub => sub.activityId));
    } catch (err) {
      console.error('Error fetching submission history:', err);
      setError(
        <Box>
          Failed to fetch submission history.
          <Button
            variant="text"
            color="inherit"
            size="small"
            onClick={() => fetchSubmissionHistory()}
            startIcon={<RefreshIcon />}
            sx={{ ml: 1 }}
          >
            Retry
          </Button>
        </Box>
      );
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleStartActivity = (activity) => {
    setSelectedActivity(activity);
    setSubmissionForm({ evidence: '', notes: '' });
    setActivityDialogOpen(true);
  };

  const handleDialogClose = () => {
    setActivityDialogOpen(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setSubmissionForm({
      ...submissionForm,
      [name]: value
    });
  };

  const handleSubmissionDetails = (submission) => {
    setSelectedSubmission(submission);
    setDetailsDialogOpen(true);
  };

  const toggleSubmissionExpand = (submissionId) => {
    if (expandedSubmission === submissionId) {
      setExpandedSubmission(null);
    } else {
      setExpandedSubmission(submissionId);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const selectedFiles = files.slice(0, 5);
    setSelectedFiles(prevFiles => [...prevFiles, ...selectedFiles]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(event.dataTransfer.files);
      const newFiles = droppedFiles.slice(0, Math.max(0, 5 - selectedFiles.length));
      setSelectedFiles(prevFiles => [...prevFiles, ...newFiles]);
    }
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleSubmitActivity = async () => {
    if (!selectedActivity) {
      setError("Please select an activity first");
      return;
    }
    setSubmitting(true);
    try {
      const tenantId = localStorage.getItem('tenantId');
      const userId = localStorage.getItem('userId');
      if (!tenantId || !userId) {
        throw new Error('User authentication information is missing');
      }
      const activityData = {
        id: selectedActivity._id || selectedActivity.id,
        name: selectedActivity.name,
        type: selectedActivity.activityType,
        amount: selectedActivity.tokenAmount
      };

      const formData = {
        activityId: activityData.id,
        evidence: submissionForm.evidence,
        notes: submissionForm.notes || '',
        userId: userId,
        tenantId: tenantId,
        activityName: activityData.name,
        activityType: activityData.type,
        tokenAmount: activityData.amount
      };

      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 10;
        setUploadProgress(Math.min(progress, 90));
        if (progress >= 90) {
          clearInterval(progressInterval);
        }
      }, 200);
      const response = await tokenEarningService.submitEvidenceWithFiles(formData, selectedFiles);
      clearInterval(progressInterval);
      setUploadProgress(100);

      const newSubmission = response.data || response;
      setSubmissionHistory(prevHistory => [newSubmission, ...prevHistory]);
      setSubmissionForm({ evidence: '', notes: '' });
      setSelectedFiles([]);
      resetFileInput();
      setUploadProgress(0);
      setError(null);
      setSnackbar({
        open: true,
        message: 'Activity submitted successfully!',
        severity: 'success'
      });
      setTabValue(1);
      setActivityDialogOpen(false);
    } catch (err) {
      console.error('Error submitting activity:', err);
      setError(`Failed to submit activity: ${err.message || 'Unknown error'}`);
      setSnackbar({
        open: true,
        message: `Failed to submit activity: ${err.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const handleClaimTokens = async (submissionId) => {
    try {
      setActionLoading(true);
      console.log(`Claiming tokens for submission: ${submissionId}`);

      const tenantId = localStorage.getItem('tenantId');
      const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');

      if (!tenantId || !authToken) {
        throw new Error('Missing tenant ID or authentication token');
      }

      const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiBaseUrl}/v1/skillcoin/activities/claim-tokens/${submissionId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'x-tenant-id': tenantId,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Server error: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('Token claiming response:', responseData);

      if (responseData.success && responseData.data && responseData.data.wallet) {
        const { wallet } = responseData.data;
        setSnackbar({
          open: true,
          message: `Tokens claimed successfully! Your new balance: ${wallet.balance} tokens`,
          severity: 'success'
        });

        fetchWalletData();

        setSubmissionHistory(prevHistory =>
          prevHistory.map(sub =>
            sub._id === submissionId
              ? { ...sub, transactionId: responseData.data.submission.transactionId }
              : sub
          )
        );
      } else {
        setSnackbar({
          open: true,
          message: 'Tokens claimed successfully!',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error claiming tokens:', error);
      setSnackbar({
        open: true,
        message: `Failed to claim tokens: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case 'content_creation':
        return <ContentIcon />;
      case 'knowledge_sharing':
        return <LearningIcon />;
      case 'forum_activity':
        return <ForumIcon />;
      case 'event_participation':
        return <EventIcon />;
      case 'learning':
        return <SchoolIcon />;
      case 'community_support':
        return <CommunityIcon />;
      default:
        return <TaskIcon />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case SUBMISSION_STATUS.APPROVED:
        return <CheckIcon />;
      case SUBMISSION_STATUS.REJECTED:
        return <CloseIcon />;
      case SUBMISSION_STATUS.IN_REVIEW:
        return <InReviewIcon />;
      case SUBMISSION_STATUS.SUBMITTED:
        return <TimeIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case SUBMISSION_STATUS.APPROVED:
        return 'Approved';
      case SUBMISSION_STATUS.REJECTED:
        return 'Rejected';
      case SUBMISSION_STATUS.IN_REVIEW:
        return 'In Review';
      case SUBMISSION_STATUS.SUBMITTED:
        return 'Submitted';
      default:
        return 'Unknown';
    }
  };

  const getStatusStepIndex = (status) => {
    switch (status) {
      case SUBMISSION_STATUS.SUBMITTED:
        return 0;
      case SUBMISSION_STATUS.IN_REVIEW:
        return 1;
      case SUBMISSION_STATUS.APPROVED:
        return 2;
      case SUBMISSION_STATUS.REJECTED:
        return 2;
      default:
        return 0;
    }
  };

  const renderFilePreview = (file, index) => {
    const isImage = file.type.startsWith('image/');
    const isPDF = file.type === 'application/pdf';
    return (
      <Box
        key={index}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          mb: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {isImage ? (
          <Box sx={{ mr: 1, width: 40, height: 40, position: 'relative' }}>
            <Avatar
              src={URL.createObjectURL(file)}
              variant="rounded"
              sx={{ width: 40, height: 40 }}
            />
          </Box>
        ) : isPDF ? (
          <PdfIcon color="error" sx={{ mr: 1 }} />
        ) : (
          <DocumentIcon color="primary" sx={{ mr: 1 }} />
        )}
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" noWrap>
            {file.name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {(file.size / 1024).toFixed(1)} KB
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => handleRemoveFile(index)}
          sx={{ ml: 1 }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  const renderFileList = (files) => {
    if (!files || files.length === 0) return null;
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Attached Evidence ({files.length})
        </Typography>
        {files.map((file, index) => {
          if (file.url && file.filename) {
            const isImage = file.contentType && file.contentType.startsWith('image/');
            const isPDF = file.contentType === 'application/pdf';

            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  mb: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                {isImage ? (
                  <Box sx={{ mr: 1, width: 40, height: 40, position: 'relative' }}>
                    <Avatar
                      src={file.url}
                      variant="rounded"
                      sx={{ width: 40, height: 40 }}
                    />
                  </Box>
                ) : isPDF ? (
                  <PdfIcon color="error" sx={{ mr: 1 }} />
                ) : (
                  <DocumentIcon color="primary" sx={{ mr: 1 }} />
                )}

                <Box sx={{ flexGrow: 1 }}>
                  <Link
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    variant="body2"
                  >
                    {file.filename}
                  </Link>
                  <Typography variant="caption" color="textSecondary" display="block">
                    {file.contentType} • {(file.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
              </Box>
            );
          }
          return renderFilePreview(file, index);
        })}
      </Box>
    );
  };

  const pendingCount = submissionHistory.filter(sub =>
    sub.status === SUBMISSION_STATUS.SUBMITTED || sub.status === SUBMISSION_STATUS.IN_REVIEW
  ).length;

  return (
    <Box sx={{ pb: 4 }}>
      <HeaderCard component={motion.div} initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <CircleDecoration size={120} top="-20px" right="-20px" opacity={0.15} />
        <CircleDecoration size={80} bottom="-10px" left="30%" opacity={0.1} />

        <CardContent sx={{ position: 'relative', zIndex: 2, p: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', mr: 2 }}>
              <RewardIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Earn {walletData?.token?.symbol || 'SkillCoin'} Tokens
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Complete activities to earn tokens and build your skills
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Box sx={{
                mt: 1,
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)'
              }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Your Token Balance
                </Typography>
                <Typography variant="h4" component="div" fontWeight="bold" sx={{ display: 'flex', alignItems: 'baseline' }}>
                  {walletData?.balance.toLocaleString() || 0}
                  <Typography component="span" variant="body1" sx={{ ml: 1, opacity: 0.8 }}>
                    {walletData?.token?.symbol || 'SKC'}
                  </Typography>
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{
                mt: 1,
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Pending Submissions
                </Typography>
                <Typography variant="h4" component="div" fontWeight="bold">
                  {pendingCount}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </HeaderCard>

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            mb: 0,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              py: 2,
              fontSize: '0.95rem',
              fontWeight: 'medium'
            },
            '& .Mui-selected': {
              fontWeight: 'bold'
            }
          }}
        >
          <Tab
            icon={<TaskIcon />}
            iconPosition="start"
            label="Available Activities"
          />
          <Tab
            icon={<HistoryIcon />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                Submission History
                {pendingCount > 0 && (
                  <Badge
                    badgeContent={pendingCount}
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
            }
          />
        </Tabs>

        {tabValue === 0 && (
          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <CircularProgress size={50} thickness={5} />
                <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
                  Loading activities...
                </Typography>
              </Box>
            ) : error ? (
              <Alert
                severity="error"
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}
              >
                {error}
              </Alert>
            ) : earningActivities.length === 0 ? (
              <Box
                component={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                sx={{ textAlign: 'center', py: 5 }}
              >
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main
                  }}
                >
                  <InfoIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  No activities available
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', mb: 4 }}>
                  There are currently no earning activities available. Please check back later or contact your administrator.
                </Typography>
                <Button
                  variant="contained"
                  onClick={fetchEarningActivities}
                  startIcon={<RefreshIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Refresh Activities
                </Button>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {earningActivities.map((activity) => {
                  const isCompleted = completedActivities.includes(activity._id);

                  return (
                    <Grid item xs={12} sm={6} md={4} key={activity._id}>
                      <ActivityCard
                        component={Paper}
                        type={activity.activityType}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ActivityHeader>
                          <Avatar
                            sx={{
                              bgcolor: alpha(getActivityColor(activity.activityType, theme), 0.1),
                              color: getActivityColor(activity.activityType, theme),
                              mr: 2
                            }}
                          >
                            {getActivityIcon(activity.activityType)}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {activity.name}
                            </Typography>
                            <Chip
                              size="small"
                              label={activity.tokenAmount + ' ' + (walletData?.token?.symbol || 'tokens')}
                              sx={{
                                fontWeight: 'bold',
                                bgcolor: alpha(getActivityColor(activity.activityType, theme), 0.1),
                                color: getActivityColor(activity.activityType, theme),
                                mt: 0.5
                              }}
                            />
                          </Box>
                        </ActivityHeader>

                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                            {activity.description}
                          </Typography>

                          <Button
                            variant={isCompleted ? "outlined" : "contained"}
                            fullWidth
                            onClick={() => handleStartActivity(activity)}
                            disabled={isCompleted}
                            startIcon={isCompleted ? <CompletedIcon /> : <ArrowIcon />}
                            sx={{ borderRadius: 2, mt: 1 }}
                          >
                            {isCompleted ? "Completed" : "Start Activity"}
                          </Button>
                        </CardContent>
                      </ActivityCard>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <CircularProgress size={50} thickness={5} />
                <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
                  Loading submission history...
                </Typography>
              </Box>
            ) : submissionHistory.length === 0 ? (
              <Box
                component={motion.div}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                sx={{ textAlign: 'center', py: 5 }}
              >
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main
                  }}
                >
                  <HistoryIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  No submissions yet
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', mb: 4 }}>
                  You haven't submitted any activities yet. Complete activities to earn tokens and see your submission history here.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setTabValue(0)}
                  sx={{ borderRadius: 2 }}
                >
                  View Available Activities
                </Button>
              </Box>
            ) : (
              <Stack spacing={3}>
                {submissionHistory.map((submission) => (
                  <Paper
                    key={submission._id}
                    elevation={1}
                    sx={{
                      p: 3,
                      mb: 3,
                      borderRadius: 3,
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{ p: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={8}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar
                              sx={{
                                mr: 2,
                                bgcolor: submission.status === SUBMISSION_STATUS.APPROVED ? 'success.light' :
                                        submission.status === SUBMISSION_STATUS.REJECTED ? 'error.light' :
                                        submission.status === SUBMISSION_STATUS.IN_REVIEW ? 'warning.light' :
                                        'primary.light'
                              }}
                            >
                              {getStatusIcon(submission.status)}
                            </Avatar>
                            <Box>
                              <Typography variant="h6" fontWeight="bold">
                                {submission.activityName || 'Activity Submission'}
                              </Typography>
                              <Chip
                                label={getStatusLabel(submission.status)}
                                size="small"
                                color={submission.status === SUBMISSION_STATUS.APPROVED ? 'success' :
                                       submission.status === SUBMISSION_STATUS.REJECTED ? 'error' :
                                       submission.status === SUBMISSION_STATUS.IN_REVIEW ? 'warning' :
                                       'primary'}
                              />
                            </Box>
                          </Box>

                          <Box sx={{ ml: 7, mt: 1 }}>
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {submission.notes || submission.evidence || 'No additional notes provided.'}
                            </Typography>

                            {submission.createdAt && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                <TimeIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                                Submitted: {new Date(submission.createdAt).toLocaleString()}
                              </Typography>
                            )}
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Box sx={{
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 2,
                            bgcolor: 'background.paper'
                          }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Token Reward
                            </Typography>
                            <Typography variant="h5" color="primary" fontWeight="bold">
                              {submission.tokenAmount || 0} {walletData?.token?.symbol || 'tokens'}
                            </Typography>

                            {submission.status === SUBMISSION_STATUS.APPROVED && !submission.transactionId && (
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                startIcon={<RewardIcon />}
                                onClick={() => handleClaimTokens(submission._id)}
                                disabled={actionLoading}
                                sx={{ mt: 2, borderRadius: 2 }}
                              >
                                {actionLoading ? 'Claiming...' : 'Claim Tokens'}
                              </Button>
                            )}

                            {submission.status === SUBMISSION_STATUS.APPROVED && submission.transactionId && (
                              <Chip
                                icon={<CheckIcon />}
                                label="Tokens Claimed"
                                color="success"
                                size="small"
                                sx={{ mt: 2 }}
                              />
                            )}
                          </Box>
                        </Grid>
                      </Grid>

                      {submission.files && submission.files.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" gutterBottom>
                            Attached Files ({submission.files.length})
                          </Typography>
                          <Grid container spacing={1}>
                            {submission.files.map((file, fileIndex) => (
                              <Grid item xs={12} sm={6} md={4} key={fileIndex}>
                                <Box
                                  sx={{
                                    p: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  <FileIcon color="primary" sx={{ mr: 1 }} />
                                  <Typography variant="body2" noWrap sx={{ flexGrow: 1 }}>
                                    {file.filename || file.name || `File ${fileIndex + 1}`}
                                  </Typography>
                                  {file.url && (
                                    <IconButton
                                      size="small"
                                      component="a"
                                      href={file.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <FileIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      )}

                      {submission.status === SUBMISSION_STATUS.REJECTED && submission.feedback && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 2 }}>
                          <Typography variant="subtitle2" color="error" gutterBottom>
                            Feedback:
                          </Typography>
                          <Typography variant="body2">
                            {submission.feedback}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Paper>

      {/* Activity Submission Dialog */}
      <Dialog
        open={activityDialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden' }
        }}
      >
        {selectedActivity && (
          <>
            <DialogTitle sx={{
              p: 3,
              display: 'flex',
              alignItems: 'center',
              background: alpha(getActivityColor(selectedActivity.activityType, theme), 0.05)
            }}>
              <Avatar
                sx={{
                  mr: 2,
                  bgcolor: alpha(getActivityColor(selectedActivity.activityType, theme), 0.1),
                  color: getActivityColor(selectedActivity.activityType, theme)
                }}
              >
                {getActivityIcon(selectedActivity.activityType)}
              </Avatar>
              <Box>
                <Typography variant="h6">{selectedActivity.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedActivity.tokenAmount} {walletData?.token?.symbol || 'tokens'} reward
                </Typography>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              <Typography variant="body1" paragraph>
                {selectedActivity.description}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Submit Your Evidence
              </Typography>

              <TextField
                name="evidence"
                label="Evidence Link or Description"
                value={submissionForm.evidence}
                onChange={handleFormChange}
                fullWidth
                required
                margin="normal"
                helperText="Provide a link to your work or describe what you did"
              />

              <TextField
                name="notes"
                label="Additional Notes"
                value={submissionForm.notes}
                onChange={handleFormChange}
                fullWidth
                multiline
                rows={3}
                margin="normal"
                helperText="Any additional information you want to share"
              />

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Upload Evidence Files (Optional)
                </Typography>

                <DropZone
                  sx={{ mb: 2 }}
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleFileDrop}
                  onDragOver={handleDragOver}
                  isDragActive={false}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                    multiple
                  />
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        mx: 'auto',
                        mb: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main
                      }}
                    >
                      <UploadIcon sx={{ fontSize: 30 }} />
                    </Avatar>
                    <Typography variant="body1" gutterBottom>
                      Drag & drop files here or click to browse
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Supports images, PDFs, and documents (max 5 files)
                    </Typography>
                  </Box>
                </DropZone>

                {selectedFiles.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {selectedFiles.map((file, index) => renderFilePreview(file, index))}
                  </Box>
                )}

                {uploadProgress > 0 && (
                  <Box sx={{ width: '100%', mt: 2 }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                    <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 1 }}>
                      Uploading... {uploadProgress}%
                    </Typography>
                  </Box>
                )}
              </Box>

              {error && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button onClick={handleDialogClose} color="inherit">
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitActivity}
                disabled={submitting || !submissionForm.evidence}
                startIcon={submitting ? <CircularProgress size={20} /> : null}
              >
                {submitting ? 'Submitting...' : 'Submit Activity'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EarningOpportunities;
